import React, { useCallback, memo } from "react";
import styles from "./MMScreenInputViews.module.css";
import { TableBody } from "@mui/material";
import { StyledTableRow, StyledTableCell, StyledTotalCell } from "./styledComponent";
import RefreshBtn from "../../../../../assets/images/RefreshBtn.svg";
import TrashBtn from "../../../../../assets/images/TrashBtn.svg";
import SingleSelect from "Samarth Module/components/dropdown/SingleSelect";
import SingleSelectWithInput from "Samarth Module/components/dropdown/SingleSelectWithInput";
import { ReasonForChange, smartProdSchedularInputFields } from "Samarth Module/constants/dailyPhasing";
// import formatNumberWithCommas from "../../../../../utils/formatNumberWithComma";
import Plus from "./../../../../../assets/images/Plus.svg";
    
// Function to get chip styling based on value
const getChipStyling = (value = 0) => {
    const num = Number(value);
    if (num === 100) {
        return { color: "#008651", backgroundColor: "#0086511A", border: "1px solid #008651" };
    }
    if (num >= 85) {
        return { color: "#FF9E00", backgroundColor: "#FF9E001A", border: "1px solid #FF9E00" };
    }
    return { color: "#FF544F", backgroundColor: "#FF544F1A", border: "1px solid #FF544F" };
};

// Memoized Single Row Component
const MemoizedRow = memo(({ row, rId, columns, renderCellContent, getRowTotal, rowReloader, rowDeleter, isActionable, addDuplicateRow }) => {

    return (
        <StyledTableRow key={`TableRow${rId}`}>
            {isActionable &&
                <StyledTableCell>
                    <img 
                        src={Plus} 
                        alt="plus icon" 
                        className={styles.plus_icon} 
                        style={{ 
                            cursor: row["Flag"] === "M2" ? "not-allowed" : "pointer",
                            opacity: row["Flag"] === "M2" ? 0.5 : 1
                        }}
                        onClick={() => row["Flag"] === "M2" ? null : addDuplicateRow(row.id, row, row.IsAddNewCBU)} 
                    />
                </StyledTableCell>}
            {columns.filter(col => col.visibility).map((cell, cId) => (
                cell.label === "Total" ? (
                    <StyledTotalCell key={`${rId}-${cId}`}>
                        {getRowTotal(row)}
                    </StyledTotalCell>
                ) : (
                    <StyledTableCell
                        key={`${rId}-${cId}`}
                        sx={cell.label === 'CBU description' || cell.label === 'Basepack' ? {
                            textAlign: "center",
                            whiteSpace: 'nowrap',
                            overflowX: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '180px'
                        } : { textAlign: "center", padding: isActionable ? '1px' : '4px !important' }}
                        title={cell.label === 'CBU description' || cell.label === 'Basepack' || cell.label === "Cascade" || cell.label === "Line" ? row[cell.label] : ""}
                    >
                        {renderCellContent(cell, row, rId, cId)}
                    </StyledTableCell>
                )
            ))}
            {/* actionable buttons in 2nd table */}
            {isActionable && (
                <>
                    <StyledTableCell>
                        <span style={{ cursor: row.isUpdated ? "pointer" : "not-allowed" }} onClick={() => row.isUpdated ? rowReloader(row.id, row.IsAddNewCBU) : null}>
                            <img src={RefreshBtn}
                                className={row.isUpdated ? styles.activeIcon : styles.deactiveIcon}
                                alt="Refresh" />
                        </span>
                    </StyledTableCell>
                    <StyledTableCell>
                        <span style={{ cursor: row?.isSubmitted ? "not-allowed" : "pointer" }} onClick={() => row?.isSubmitted ? null : rowDeleter(row.id, row.IsAddNewCBU, row)}>
                            <img src={TrashBtn} className={row.isAdded ? styles.activeIcon : styles.deactiveIcon} alt="Delete icon" />
                        </span>

                    </StyledTableCell>
                </>
            )}
        </StyledTableRow>
    );
}, (prevProps, nextProps) => {
    // Only rerender if the row data or actionable flag changed
    return JSON.stringify(prevProps.row) === JSON.stringify(nextProps.row) &&
        prevProps.isActionable === nextProps.isActionable &&
        JSON.stringify(prevProps.columns) === JSON.stringify(nextProps.columns)
});

const TblBody = ({ columns, body, preHandleInputChange, handleInputChange, rowResetHandler, rowDeleteHandler, isActionable = false, addDuplicateRow }) => {


    const getRowTotal = useCallback((row) => {
        const result = smartProdSchedularInputFields.reduce((sum, day) => sum + (Number(row[day]) || 0), 0);
        return Number(Number(result).toFixed(2))
    }, []);


    const renderCellContent = useCallback((cell, row, rId, cId) => {
        const cellValue = (row[cell.label] === undefined || row[cell.label] === null || row[cell.label] === "")
            ? ""
            : cell.label === "SAP weekly plan"
                ? row[cell.label] === ""
                    ? ""
                    : !isNaN(row[cell.label]) ? Number(Number(row[cell.label]).toFixed(2)) || 0 : row[cell.label]
                : smartProdSchedularInputFields.includes(cell.label)
                    ? row[cell.label] === ""
                        ? ""
                        : !isNaN(row[cell.label]) ? row[cell.label] !== 0 ? Number(Number(row[cell.label]).toFixed(2)) : 0 : row[cell.label]
                    : row[cell.label];


        if (cell.input) {
            return (
                <input
                    type={smartProdSchedularInputFields.includes(cell.label) ? "number" : "text"}
                    key={`${cell.label}${rId}${cId}`}
                    id={`${cell.label}${rId}${cId}`}
                    className={
                        cell.label === "Cascade" ? styles.mmCascadeInputField
                            : cell.label === "Line" ? styles.mmLineInputField
                                : styles.mmDayInputField
                    }
                    name={cell.label}
                    value={cellValue}
                    disabled={row["Flag"] === "S1"}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => e.key === 'e' && e.preventDefault()}
                    onChange={(e) => {
                        preHandleInputChange(row.id, cell.label, row.IsAddNewCBU, e.target.value);
                        handleInputChange(row.id, cell.label, row.IsAddNewCBU, e.target.value);
                    }}
                />
            );
        }

        if (cell.chip) {
            return (
                <span className={styles.chip} style={getChipStyling(cellValue || 0)}>
                    {`${cellValue || 0}%`}
                </span>
            );
        }

        if (cell.dropdown) {
            const options = [
                ...ReasonForChange,
                row["Comment For Reason"] ? `Others, ${row["Comment For Reason"]}` : "Others"
            ];

            return cell.label === "Reason for change" ? (
                <SingleSelect
                    key={`${cell.label}${rId}${cId}`}
                    data={row}
                    field={cell.label}
                    placeholder={cell.label === "Reason for change" ? "Select reason" : ""}
                    value={cellValue}
                    options={options}
                    isDisabled={row["Flag"] === "S1"}
                    onChange={(val) => handleInputChange(row.id, cell.label, row.IsAddNewCBU, val)}
                />
            ) : (
                <SingleSelectWithInput
                    key={`${cell.label}${rId}${cId}`}
                    title={cellValue}
                    placeholder={cell.label}
                    width={"200px"}
                    label={cell.label}
                    value={cellValue}
                    disabled={row["Flag"] === "S1"}
                    options={cell.label === "Cascade" ? row.cascadeOptions : row.lineOptions}
                    onChangeHandler={(_, value) => handleInputChange(row.id, cell.label, row.IsAddNewCBU, value)}
                />
            )
        }

        return cellValue;
    }, [handleInputChange, preHandleInputChange]);

    const rowReloader = useCallback((id, IsAddNewCBU) => {
        rowResetHandler(id, IsAddNewCBU)
    }, [rowResetHandler])

    const rowDeleter = useCallback((id, IsAddNewCBU, row) => {
        rowDeleteHandler(id, IsAddNewCBU, row)
    }, [rowDeleteHandler])

    return (
        <TableBody>
            {body.length > 0 ? body.map((row, rId) => (
                <MemoizedRow
                    key={`MemoizedRow${rId}`}
                    row={row}
                    rId={rId}
                    columns={[...columns]}
                    renderCellContent={renderCellContent}
                    getRowTotal={getRowTotal}
                    rowReloader={rowReloader}
                    rowDeleter={rowDeleter}
                    isActionable={isActionable}
                    addDuplicateRow={addDuplicateRow}
                />
            )) : (
                <StyledTableRow>
                    <StyledTableCell colSpan={columns ? columns.length : 1} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        Data is loading...
                    </StyledTableCell>
                </StyledTableRow>
            )}
        </TableBody>
    );
};

export default memo(TblBody);
